import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const money = v => v == null || v === '' ? '—' : '₹' + Number(v).toLocaleString('en-IN');
const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '—';
const PO_ST = ['draft','ordered','received','invoiced','closed','cancelled'];
const WC_ST = ['open','submitted','approved','rejected','replaced','closed'];
const AMC_ST = ['active','expired','cancelled'];
const stColor = s => ({ active:'var(--green)', expired:'var(--red)', cancelled:'var(--text2)', open:'var(--orange)',
  submitted:'var(--accent)', approved:'var(--green)', rejected:'var(--red)', replaced:'var(--green)',
  closed:'var(--text2)', draft:'var(--text2)', ordered:'var(--accent)', received:'var(--orange)', invoiced:'var(--green)' }[s] || 'var(--text2)');
const Pill = ({ s }) => <span style={{ fontSize:11, fontWeight:600, color:stColor(s), background:'var(--bg3)', padding:'2px 8px', borderRadius:10 }}>{s}</span>;

export default function VendorManagement() {
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get('/vendors').then(r => setVendors(r.data)).catch(()=>{}), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>🤝 Vendor Management</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>AMC contracts, purchase orders, warranty claims, service history &amp; quotations — per vendor. Manage the vendor directory under Master Data.</p>
      </div>
      {!vendors.length ? <div className="card"><div className="empty-state"><p>No active vendors. Add them in Master Data → Vendors.</p></div></div> : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:14 }}>
          {vendors.map(v => (
            <div key={v.id} className="card" style={{ padding:16, cursor:'pointer' }} onClick={() => setOpen(v.id)}>
              <div style={{ fontWeight:600 }}>{v.name}</div>
              <div style={{ fontSize:12, color:'var(--text2)' }}>{v.service_categories || '—'}</div>
              {(v.contact_person || v.phone) && <div style={{ fontSize:12, color:'var(--text2)', marginTop:3 }}>{v.contact_person}{v.phone?` · ${v.phone}`:''}</div>}
              <div className="flex-gap" style={{ gap:6, marginTop:10, flexWrap:'wrap' }}>
                <span className="badge badge-completed">{v.amc_active} AMC</span>
                {v.amc_expiring>0 && <span className="badge badge-repair">⚠ {v.amc_expiring} expiring</span>}
                {v.po_open>0 && <span className="badge badge-normal">{v.po_open} PO open</span>}
                {v.claims_open>0 && <span className="badge badge-pending">{v.claims_open} claim(s)</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && <VendorDetail id={open} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function VendorDetail({ id, onClose }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState('amc');
  const [campuses, setCampuses] = useState([]);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => api.get(`/vendors/${id}/profile`).then(r => setD(r.data)).catch(()=>{}), [id]);
  useEffect(() => { load(); api.get('/master/campuses').then(r => setCampuses(r.data.filter(c=>c.active))).catch(()=>{}); }, [load]);
  if (!d) return null;
  const flash = m => { setMsg(m); setTimeout(()=>setMsg(''),2500); };
  const upd = async (path, patch) => { try { await api.put(path, patch); load(); } catch(e){ flash('Failed'); } };
  const del = async (path) => { if (window.confirm('Delete this record?')) { await api.delete(path); load(); } };

  const TABS = [['amc',`AMC (${d.amc.length})`],['pos',`Purchase Orders (${d.purchase_orders.length})`],
    ['warranty',`Warranty (${d.claims.length})`],['service',`Service History (${d.service.length})`],['quotes',`Quotations (${d.quotes.length})`]];

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:900, maxHeight:'92vh', overflowY:'auto' }}>
        <div className="modal-header">
          <span className="modal-title">🤝 {d.vendor.name}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12 }}>
          {d.vendor.contact_person || '—'}{d.vendor.phone?` · ${d.vendor.phone}`:''}{d.vendor.email?` · ${d.vendor.email}`:''}
          {d.vendor.sla_days?` · SLA ${d.vendor.sla_days}d`:''}
        </div>
        <div className="flex-gap" style={{ gap:6, marginBottom:14, flexWrap:'wrap' }}>
          {TABS.map(([t,l]) => <button key={t} className={`btn btn-sm ${tab===t?'btn-primary':'btn-secondary'}`} onClick={()=>setTab(t)}>{l}</button>)}
        </div>

        {tab==='amc' && <AmcTab d={d} id={id} campuses={campuses} upd={upd} del={del} flash={flash} reload={load} />}
        {tab==='pos' && <PoTab d={d} id={id} campuses={campuses} upd={upd} del={del} flash={flash} reload={load} />}
        {tab==='warranty' && <WarrantyTab d={d} id={id} campuses={campuses} upd={upd} del={del} flash={flash} reload={load} />}
        {tab==='service' && (
          <div className="table-wrap"><table>
            <thead><tr><th>Ticket</th><th>Date</th><th>Issue</th><th>Status</th><th>Cost</th></tr></thead>
            <tbody>{d.service.map(s => <tr key={s.id}><td className="mono" style={{fontSize:12}}>{s.ticket_number||'—'}</td>
              <td style={{fontSize:12}}>{fmt(s.service_date)}</td><td style={{fontSize:12}}>{s.issue_desc||'—'}</td>
              <td><Pill s={s.service_status||s.status||'—'} /></td><td style={{fontSize:12}}>{money(s.total_cost)}</td></tr>)}
              {!d.service.length && <tr><td colSpan="5" style={{color:'var(--text2)',fontSize:12,padding:10}}>No service records.</td></tr>}</tbody>
          </table></div>
        )}
        {tab==='quotes' && (
          <div className="table-wrap"><table>
            <thead><tr><th>Ticket</th><th>Date</th><th>Amount</th><th>Approval</th></tr></thead>
            <tbody>{d.quotes.map(q => <tr key={q.id}><td className="mono" style={{fontSize:12}}>{q.ticket_number||'—'}</td>
              <td style={{fontSize:12}}>{fmt(q.quote_date)}</td><td style={{fontSize:12}}>{money(q.amount)}</td><td><Pill s={q.approval_status||'—'} /></td></tr>)}
              {!d.quotes.length && <tr><td colSpan="4" style={{color:'var(--text2)',fontSize:12,padding:10}}>No quotations.</td></tr>}</tbody>
          </table></div>
        )}
        {msg && <div style={{ fontSize:13, marginTop:10, color:'var(--red)' }}>{msg}</div>}
      </div>
    </div>
  );
}

function AmcTab({ d, id, campuses, upd, del, flash, reload }) {
  const [f, setF] = useState({ title:'', start_date:'', end_date:'', amount:'', campus_id:'', coverage:'' });
  const add = async () => { if (!f.title.trim()) { flash('Title required'); return; }
    try { await api.post(`/vendors/${id}/amc`, f); setF({ title:'', start_date:'', end_date:'', amount:'', campus_id:'', coverage:'' }); reload(); } catch(e){ flash('Failed'); } };
  return (<>
    <div className="table-wrap"><table>
      <thead><tr><th>Title</th><th>Coverage</th><th>Start</th><th>End</th><th>Amount</th><th>Status</th><th></th></tr></thead>
      <tbody>{d.amc.map(a => (
        <tr key={a.id}><td style={{fontSize:12,fontWeight:500}}>{a.title}{a.asset_tag&&<div style={{color:'var(--text2)',fontSize:11}}>{a.asset_tag}</div>}</td>
          <td style={{fontSize:12,color:'var(--text2)'}}>{a.coverage||'—'}</td><td style={{fontSize:12}}>{fmt(a.start_date)}</td><td style={{fontSize:12}}>{fmt(a.end_date)}</td>
          <td style={{fontSize:12}}>{money(a.amount)}</td>
          <td><select value={a.status} onChange={e=>upd(`/vendors/amc/${a.id}`,{status:e.target.value})} style={{fontSize:11,padding:'2px 4px'}}>{AMC_ST.map(s=><option key={s}>{s}</option>)}</select>
            {a.eff_status==='expired'&&a.status!=='cancelled'&&<span style={{color:'var(--red)',fontSize:10,marginLeft:4}}>expired</span>}</td>
          <td><button className="btn btn-danger btn-sm" onClick={()=>del(`/vendors/amc/${a.id}`)}>×</button></td></tr>
      ))}{!d.amc.length && <tr><td colSpan="7" style={{color:'var(--text2)',fontSize:12,padding:10}}>No AMC contracts.</td></tr>}</tbody>
    </table></div>
    <div className="card" style={{ marginTop:10 }}>
      <div className="form-row">
        <div className="form-group" style={{flex:2}}><label>New AMC title</label><input value={f.title} onChange={e=>setF(s=>({...s,title:e.target.value}))} placeholder="e.g. Firewall AMC 2026" /></div>
        <div className="form-group"><label>Start</label><input type="date" value={f.start_date} onChange={e=>setF(s=>({...s,start_date:e.target.value}))} /></div>
        <div className="form-group"><label>End</label><input type="date" value={f.end_date} onChange={e=>setF(s=>({...s,end_date:e.target.value}))} /></div>
        <div className="form-group"><label>Amount</label><input type="number" value={f.amount} onChange={e=>setF(s=>({...s,amount:e.target.value}))} /></div>
      </div>
      <button className="btn btn-primary btn-sm" onClick={add}>+ Add AMC</button>
    </div>
  </>);
}

function PoTab({ d, id, campuses, upd, del, flash, reload }) {
  const [f, setF] = useState({ po_number:'', description:'', amount:'', order_date:'', campus_id:'' });
  const add = async () => { if (!f.description.trim()) { flash('Description required'); return; }
    try { await api.post(`/vendors/${id}/pos`, f); setF({ po_number:'', description:'', amount:'', order_date:'', campus_id:'' }); reload(); } catch(e){ flash('Failed'); } };
  return (<>
    <div className="table-wrap"><table>
      <thead><tr><th>PO #</th><th>Description</th><th>Amount</th><th>Status</th><th>Invoice</th><th></th></tr></thead>
      <tbody>{d.purchase_orders.map(p => (
        <tr key={p.id}><td className="mono" style={{fontSize:12}}>{p.po_number||'—'}</td><td style={{fontSize:12}}>{p.description}</td>
          <td style={{fontSize:12}}>{money(p.amount)}</td>
          <td><select value={p.status} onChange={e=>upd(`/vendors/pos/${p.id}`,{status:e.target.value})} style={{fontSize:11,padding:'2px 4px'}}>{PO_ST.map(s=><option key={s}>{s}</option>)}</select></td>
          <td><input defaultValue={p.invoice_no||''} placeholder="inv #" onBlur={e=>{ if(e.target.value!==(p.invoice_no||'')) upd(`/vendors/pos/${p.id}`,{invoice_no:e.target.value}); }} style={{fontSize:11,padding:'2px 4px',width:80}} /></td>
          <td><button className="btn btn-danger btn-sm" onClick={()=>del(`/vendors/pos/${p.id}`)}>×</button></td></tr>
      ))}{!d.purchase_orders.length && <tr><td colSpan="6" style={{color:'var(--text2)',fontSize:12,padding:10}}>No purchase orders.</td></tr>}</tbody>
    </table></div>
    <div className="card" style={{ marginTop:10 }}>
      <div className="form-row">
        <div className="form-group"><label>PO #</label><input value={f.po_number} onChange={e=>setF(s=>({...s,po_number:e.target.value}))} /></div>
        <div className="form-group" style={{flex:2}}><label>Description</label><input value={f.description} onChange={e=>setF(s=>({...s,description:e.target.value}))} placeholder="e.g. 5× switches" /></div>
        <div className="form-group"><label>Amount</label><input type="number" value={f.amount} onChange={e=>setF(s=>({...s,amount:e.target.value}))} /></div>
        <div className="form-group"><label>Order date</label><input type="date" value={f.order_date} onChange={e=>setF(s=>({...s,order_date:e.target.value}))} /></div>
      </div>
      <button className="btn btn-primary btn-sm" onClick={add}>+ Add PO</button>
    </div>
  </>);
}

function WarrantyTab({ d, id, campuses, upd, del, flash, reload }) {
  const [f, setF] = useState({ issue:'', claim_date:'', campus_id:'' });
  const add = async () => { if (!f.issue.trim()) { flash('Issue required'); return; }
    try { await api.post(`/vendors/${id}/warranty`, f); setF({ issue:'', claim_date:'', campus_id:'' }); reload(); } catch(e){ flash('Failed'); } };
  return (<>
    <div className="table-wrap"><table>
      <thead><tr><th>Date</th><th>Issue</th><th>Asset</th><th>Status</th><th>Resolution</th><th></th></tr></thead>
      <tbody>{d.claims.map(w => (
        <tr key={w.id}><td style={{fontSize:12}}>{fmt(w.claim_date)}</td><td style={{fontSize:12}}>{w.issue}</td><td style={{fontSize:12}}>{w.asset_tag||'—'}</td>
          <td><select value={w.status} onChange={e=>upd(`/vendors/warranty/${w.id}`,{status:e.target.value})} style={{fontSize:11,padding:'2px 4px'}}>{WC_ST.map(s=><option key={s}>{s}</option>)}</select></td>
          <td><input defaultValue={w.resolution||''} placeholder="—" onBlur={e=>{ if(e.target.value!==(w.resolution||'')) upd(`/vendors/warranty/${w.id}`,{resolution:e.target.value}); }} style={{fontSize:11,padding:'2px 4px',width:120}} /></td>
          <td><button className="btn btn-danger btn-sm" onClick={()=>del(`/vendors/warranty/${w.id}`)}>×</button></td></tr>
      ))}{!d.claims.length && <tr><td colSpan="6" style={{color:'var(--text2)',fontSize:12,padding:10}}>No warranty claims.</td></tr>}</tbody>
    </table></div>
    <div className="card" style={{ marginTop:10 }}>
      <div className="form-row">
        <div className="form-group" style={{flex:2}}><label>New claim — issue</label><input value={f.issue} onChange={e=>setF(s=>({...s,issue:e.target.value}))} placeholder="e.g. DOA switch, RMA needed" /></div>
        <div className="form-group"><label>Claim date</label><input type="date" value={f.claim_date} onChange={e=>setF(s=>({...s,claim_date:e.target.value}))} /></div>
      </div>
      <button className="btn btn-primary btn-sm" onClick={add}>+ Add Claim</button>
    </div>
  </>);
}
